import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableRow, Paper } from '@mui/material';
import {InfoGrid, tableCellStyle } from '../../../../../utils/reportUtils';
import { OocyteRetrievalType } from '../../../../../components/Surgery/oocyteRetrieval/OocyteRetrival.helper';
import { useTranslation } from 'react-i18next';
import { ClinicHeader } from '../../../../../components/Reports/ClinicHeader';
import PatientDetailsHeader from '../../../../../components/Reports/PatientDetailsHeader';
import { formatDate } from '../../../../../utils/dateUtils';

interface Props {
  data: OocyteRetrievalType;
}

const OocyteRetrievalReport: React.FC<Props> = ({ data }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');


  
  const infoGridData: string[][] = [
    [tGeneral('clinician'), data.clinician_name || '', tGeneral('embryologist'), data.embryologist_name || ''],
    [tSurgery('witnessEmbryologist'), data.witness_embryologist_name || '', tGeneral('anesthetist'), data.anesthetist_name || ''],
    [tGeneral('nurse'), data.nurse_name || '', tSurgery('anesthesiaType'), data.type_of_anesthesia_label || ''],
    [tSurgery('needleType'), data.type_of_needle_label || '', tSurgery('subType'), data.sub_type_of_needle_label || ''],
    [
      tSurgery('difficultyLevel'),
      data.difficulty_level_label || '',
      tSurgery('pacDone'),
      data.pre_anesthesia_checkup == null ? '' : data.pre_anesthesia_checkup ? tGeneral('Yes') : tGeneral('No'),
    ],
  ];

  const timingDetails: string[][] = [
    [tSurgery('cycleStartDate'), formatDate(data.cycle_start_date) ?? '-', tSurgery('opuDate'), formatDate(data.opu_date) ?? '-'],
    [tGeneral('startTime'), data.start_time ?? '-', tGeneral('endTime'), data.end_time ?? '-'],
  ];

  const currentDateTime = new Date().toLocaleString();

  return (
    <Box sx={{ p: 5, backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif', position: 'relative', minHeight: '100%' }}>
      <ClinicHeader />
      <PatientDetailsHeader />

      <Box component="hr" sx={{ my: 1, borderColor: '#fff' }} />

      {/* Clinical Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{tSurgery('clinicalAndProcedureInfo')}</Typography>
        <InfoGrid  data={infoGridData} />
      </Box>

      {/* Timing Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>{tSurgery('timingAndOtherDetails')}</Typography>
        <InfoGrid data={timingDetails} />
      </Box>

      {/* Follicles and Oocytes */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{tSurgery('folliclesAndOocytes')}</Typography>
        <Paper>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={tableCellStyle}>{tSurgery('leftFollicles')}</TableCell>
                <TableCell sx={tableCellStyle}>{(data.optimum_follicles?.left_follicles ?? data.optimum_follicles_left) ?? '-'}</TableCell>
                <TableCell sx={tableCellStyle}>{tSurgery('rightFollicles')}</TableCell>
                <TableCell sx={tableCellStyle}>{(data.optimum_follicles?.right_follicles ?? data.optimum_follicles_right) ?? '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={tableCellStyle}>{tSurgery('oocytesRetrievedLeft')}</TableCell>
                <TableCell sx={tableCellStyle}>{data.oocytes_retrieved_left ?? '-'}</TableCell>
                <TableCell sx={tableCellStyle}>{tSurgery('oocytesRetrievedRight')}</TableCell>
                <TableCell sx={tableCellStyle}>{data.oocytes_retrieved_right ?? '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Procedure Findings */}
      {data.procedure_findings && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{tSurgery('procedureFindings')}</Typography>
          <Typography variant="body2">{data.procedure_findings}</Typography>
        </Box>
      )}

      {/* Notes */}
      {data.notes && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{tGeneral('notes')}</Typography>
          <Typography variant="body2">{data.notes}</Typography>
        </Box>
      )}

      {/* Footer Timestamp */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          color: '#666',
          fontStyle: 'italic',
        }}
      >
        {tGeneral('reportGeneratedOn')}: {currentDateTime}
      </Typography>
    </Box>
  );
};

export default OocyteRetrievalReport;
